import * as go from 'gojs/release/go-module';

import {
  LinkCategory,
  LinkLabelCategory,
  NodeCategory,
} from 'src/containers/ProjectDiagram/constants/categories';
import { GeometryString } from 'src/containers/ProjectDiagram/constants/geometries';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import {
  FILE_UPLOAD_ICON_WIDTH_RATIO,
  LINK_GRAB_HANDLE_SIZE,
  LINK_HANDLE_FILL_COLOR,
  LINK_HANDLE_STROKE_COLOR,
  NODE_SIZE,
  RELINK_HALO_STROKE_COLOR,
  RELINK_HALO_STROKE_WIDTH,
  VIRTUAL_GRAPH_ICON_WIDTH_RATIO,
} from 'src/containers/ProjectDiagram/constants/styles';
import {
  Diagram,
  MappingLink,
  ModelConceptClassNode,
  ModelConceptRelationshipLabelNode,
  ModelConceptRelationshipLink,
  ResourceNode,
} from 'src/containers/ProjectDiagram/types/diagram';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isFileUpload } from 'src/containers/ProjectDiagram/utils/data';
import {
  isLink,
  isLinkLabel,
  isNode,
} from 'src/containers/ProjectDiagram/utils/graphObject';
import { addMappingsToConnectedValidRelationships } from 'src/containers/ProjectDiagram/utils/mapping/addMappingsToConnectedValidRelationships';
import { changeMappingConnection } from 'src/containers/ProjectDiagram/utils/mapping/changeMappingConnection';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { addModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addModelConceptRelationship';
import { changeModelConceptRelationshipConnection } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/changeModelConceptRelationshipConnection';

const $ = go.GraphObject.make;

const TEMPORARY_DYNAMIC_SHAPE_NODE = $(
  go.Node,
  {
    selectable: false,
    layerName: 'Tool',
  },
  $(go.Shape, {
    portId: '',
    name: 'DYNAMIC_HALO',
    figure: 'Circle',
    fill: null,
    stroke: RELINK_HALO_STROKE_COLOR,
    strokeWidth: RELINK_HALO_STROKE_WIDTH,
    desiredSize: new go.Size(1, 1),
  })
);

const TEMPORARY_FILE_UPLOAD_NODE = $(
  go.Node,
  {
    selectable: false,
    layerName: 'Tool',
  },
  $(go.Shape, {
    portId: '',
    name: 'FILE_UPLOAD_HALO',
    width: NODE_SIZE * FILE_UPLOAD_ICON_WIDTH_RATIO,
    height: NODE_SIZE,
    fill: null,
    stroke: RELINK_HALO_STROKE_COLOR,
    strokeWidth: RELINK_HALO_STROKE_WIDTH,
    geometryString: GeometryString.FILE_UPLOAD_HALO,
  })
);

const TEMPORARY_VIRTUAL_GRAPH_NODE = $(
  go.Node,
  {
    selectable: false,
    layerName: 'Tool',
  },
  $(go.Shape, {
    portId: '',
    name: 'VIRTUAL_GRAPH_HALO',
    width: NODE_SIZE * VIRTUAL_GRAPH_ICON_WIDTH_RATIO,
    height: NODE_SIZE,
    fill: null,
    stroke: RELINK_HALO_STROKE_COLOR,
    strokeWidth: RELINK_HALO_STROKE_WIDTH,
    geometryString: GeometryString.VIRTUAL_GRAPH_HALO,
  })
);

const createHandleArchetype = (to: boolean) =>
  $(go.Shape, {
    figure: 'Diamond',
    desiredSize: new go.Size(LINK_GRAB_HANDLE_SIZE, LINK_GRAB_HANDLE_SIZE),
    fill: LINK_HANDLE_FILL_COLOR,
    stroke: LINK_HANDLE_STROKE_COLOR,
    cursor: 'pointer',
    segmentIndex: to ? -1 : 0,
  });

// Extended to behave like you were dragging a link's head/tail in realtime.
export class ExtendedRelinkingTool extends go.RelinkingTool {
  // @ts-ignore todo: fix accessor types
  diagram!: Diagram;

  // @ts-ignore todo: fix accessor types
  linkingCursor = 'grabbing';

  // @ts-ignore todo: fix accessor types
  portGravity = 0;

  temporaryLinkLabel: go.Node | null = null;

  // @ts-ignore todo: fix accessor types
  temporaryFromNode = TEMPORARY_DYNAMIC_SHAPE_NODE.copy();

  // @ts-ignore todo: fix accessor types
  temporaryToNode = TEMPORARY_DYNAMIC_SHAPE_NODE.copy();

  // @ts-ignore todo: fix accessor types
  fromHandleArchetype = createHandleArchetype(false);

  // @ts-ignore todo: fix accessor types
  toHandleArchetype = createHandleArchetype(true);

  setHandle(): boolean {
    const handle =
      this.findToolHandleAt(
        this.diagram.firstInput.documentPoint,
        'RelinkFrom'
      ) ||
      this.findToolHandleAt(this.diagram.firstInput.documentPoint, 'RelinkTo');
    if (!handle) {
      return false;
    }

    const { part } = handle;
    if (
      !(part instanceof go.Adornment && part.adornedPart instanceof go.Link)
    ) {
      return false;
    }

    this.handle = handle;
    this.isForwards = part.category === 'RelinkTo';

    const link = part.adornedPart;
    const linkLabel = link.labelNodes.first();

    this.originalLink = link;

    this.temporaryLink = link.copy();
    // Since gojs can use object references for lookup, we need to replace `data`.
    // This `data` isn't actually used for anything since the link and node
    // already copied the full Panel tree, and `data` is only used when with a Model.
    this.temporaryLink.data = null;

    if (linkLabel) {
      this.temporaryLinkLabel = linkLabel.copy();
      this.temporaryLinkLabel.labeledLink = this.temporaryLink;
      this.temporaryLinkLabel.data = null;
    }

    return true;
  }

  doActivate() {
    commitDiagramModel(
      this.diagram,
      (_model, diagram) => {
        // We have to do our own lookup for originalLink since super.doActivate() relies
        // on this.temporaryLink, and we want to create a temporaryLink based on originalLink.
        const didSetHandle = this.setHandle();
        if (!didSetHandle) {
          return;
        }

        // Update which halo shapes to show, and whether the stroke is visible
        let fromHalo;
        if (
          isLink<MappingLink>(
            this.originalLink as go.Link,
            LinkCategory.MAPPING
          )
        ) {
          if (
            this.originalLink?.fromNode?.data &&
            isFileUpload(this.originalLink?.fromNode?.data)
          ) {
            this.temporaryFromNode = TEMPORARY_FILE_UPLOAD_NODE.copy();
            fromHalo = this.temporaryFromNode.findObject(
              'FILE_UPLOAD_HALO'
            ) as go.Shape;
          } else {
            this.temporaryFromNode = TEMPORARY_VIRTUAL_GRAPH_NODE.copy();
            fromHalo = this.temporaryFromNode.findObject(
              'VIRTUAL_GRAPH_HALO'
            ) as go.Shape;
          }
        } else {
          this.temporaryFromNode = TEMPORARY_DYNAMIC_SHAPE_NODE.copy();
          fromHalo = this.temporaryFromNode.findObject(
            'DYNAMIC_HALO'
          ) as go.Shape;
        }

        this.temporaryToNode = TEMPORARY_DYNAMIC_SHAPE_NODE.copy();
        const toHalo = this.temporaryToNode.findObject(
          'DYNAMIC_HALO'
        ) as go.Shape;

        fromHalo.stroke = this.isForwards
          ? 'transparent'
          : RELINK_HALO_STROKE_COLOR;
        toHalo.stroke = this.isForwards
          ? RELINK_HALO_STROKE_COLOR
          : 'transparent';

        // starts the transaction and adds the temporary link to the diagram
        super.doActivate();

        // also add the temporary link label
        if (this.temporaryLinkLabel) {
          diagram.add(this.temporaryLinkLabel);
        }

        // hide the original link
        if (this.originalLink) {
          this.originalLink.visible = false;
        }
      },
      { shouldCommit: false }
    );
  }

  copyPortProperties(
    realNode: go.Node | null,
    realPort: go.GraphObject | null,
    tempNode: go.Node,
    tempPort: go.GraphObject,
    toEnd: boolean
  ) {
    // Change the link's temporaryToNode shape if we are a Mapping link.
    // Mapping links can connect to either a ModelConcept Class (Circle) or Relationship (RoundedRectangle)
    if (
      toEnd &&
      realNode &&
      isLink<MappingLink>(this.originalLink as go.Link, LinkCategory.MAPPING)
    ) {
      const shape = tempNode.findObject('DYNAMIC_HALO') as go.Shape;
      shape.figure = isLinkLabel<ModelConceptRelationshipLabelNode>(
        realNode,
        LinkLabelCategory.MODEL_CONCEPT_RELATIONSHIP_LABEL
      )
        ? 'RoundedRectangle'
        : 'Circle';
    }

    super.copyPortProperties(realNode, realPort, tempNode, tempPort, toEnd);
  }

  reconnectLink(
    existingLink: go.Link,
    newNode: go.Node | null,
    _newPort: go.GraphObject | null,
    isTo: boolean
  ): boolean {
    // Don't call super.reconnectLink because then changeModelConceptRelationshipConnection
    // will return early and won't set new keys!

    if (!newNode) {
      return false;
    }
    const { diagram } = this;
    const { model } = diagram;

    // link class relationship to another class
    if (
      isLink<ModelConceptRelationshipLink>(
        existingLink,
        LinkCategory.MODEL_CONCEPT_RELATIONSHIP
      ) &&
      isNode<ModelConceptClassNode>(newNode, NodeCategory.MODEL_CONCEPT_CLASS)
    ) {
      const fromKey = !isTo ? newNode.key : existingLink.data.from;
      const toKey = isTo ? newNode.key : existingLink.data.to;
      const fromIri = fromKey ? getModelConceptClassValue(fromKey) : undefined;
      const toIri = toKey ? getModelConceptClassValue(toKey) : undefined;

      const { linkData: reconnectedLinkData } =
        changeModelConceptRelationshipConnection({
          key: existingLink.key,
          fromIri,
          toIri,
        })(model);

      const { addedRelationships } = addModelConceptRelationship({
        direction: isTo
          ? RelationshipDirection.INCOMING
          : RelationshipDirection.OUTGOING,
        iri: existingLink.data.value,
        classIri: newNode.data.value,
        addUnconnectedRelationship: false,
      })(model, diagram);

      const affectedRelationships = reconnectedLinkData
        ? [reconnectedLinkData, ...addedRelationships]
        : [...addedRelationships];

      addMappingsToConnectedValidRelationships(affectedRelationships)(model);

      return true;
    }

    // link a class OR a class's relationship to a resource
    if (isLink<MappingLink>(existingLink, LinkCategory.MAPPING)) {
      if (!isTo) {
        console.warn(
          new Error('Modifying source resources for mappings is not supported')
        );
        return false;
      }

      changeMappingConnection({
        key: existingLink.key,
        toKey: newNode.key as string,
      })(model);

      return true;
    }

    return false;
  }

  doDeactivate() {
    commitDiagramModel(
      this.diagram,
      (_model, diagram) => {
        // show the original link
        if (this.originalLink) {
          this.originalLink.visible = true;
        }

        // remove the temporary link label
        if (this.temporaryLinkLabel) {
          diagram.remove(this.temporaryLinkLabel);
          this.temporaryLinkLabel = null;
        }

        // remove the temporary link from the diagram and stops the transaction,
        // which will make a commit in the UndoManager
        super.doDeactivate();
      },
      {
        layout: DiagramModelCommitLayout.PARTIAL,
        shouldCommit: false,
      }
    );
  }

  // @ts-ignore todo: fix accessor types
  linkValidation = (
    // these can be null even though gojs doesn't type them as so
    fromNode: go.Node | null,
    _fromPort: go.GraphObject | null,
    toNode: go.Node | null,
    _toPort: go.GraphObject | null,
    link: go.Link
  ) => {
    if (!fromNode && !toNode) {
      return false;
    }

    // Is this a valid relationship?
    if (
      isLink<ModelConceptRelationshipLink>(
        link,
        LinkCategory.MODEL_CONCEPT_RELATIONSHIP
      ) &&
      (!fromNode ||
        isNode<ModelConceptClassNode>(
          fromNode,
          NodeCategory.MODEL_CONCEPT_CLASS
        )) &&
      (!toNode ||
        isNode<ModelConceptClassNode>(toNode, NodeCategory.MODEL_CONCEPT_CLASS))
    ) {
      const alreadyHasFrom = fromNode?.linksConnected.any(
        (otherLink) =>
          isLink<ModelConceptRelationshipLink>(
            otherLink,
            LinkCategory.MODEL_CONCEPT_RELATIONSHIP
          ) &&
          otherLink.data.value === link.data.value &&
          otherLink.data.from === fromNode.key
      );
      const alreadyHasTo = toNode?.linksConnected.any(
        (otherLink) =>
          isLink<ModelConceptRelationshipLink>(
            otherLink,
            LinkCategory.MODEL_CONCEPT_RELATIONSHIP
          ) &&
          otherLink.data.value === link.data.value &&
          otherLink.data.to === toNode.key
      );

      if (fromNode && toNode) {
        // We're trying to link an unknown link end from a node (that obviously already has this link),
        // to another node which also has the same link.
        return !alreadyHasFrom || !alreadyHasTo;
      }
      if (fromNode && !toNode) {
        // We're trying to link the "from" end of a fully-detached link to a node
        // that already has this link.
        return !alreadyHasFrom;
      }
      if (!fromNode && toNode) {
        // We're trying to link the "to" end of a fully-detached link to a node
        // that already has this link.
        return !alreadyHasTo;
      }

      // unreachable
      return false;
    }

    // Is this a valid mapping?
    if (
      isLink<MappingLink>(link, LinkCategory.MAPPING) &&
      fromNode &&
      isNode<ResourceNode>(fromNode, NodeCategory.RESOURCE) &&
      toNode &&
      (isNode<ModelConceptClassNode>(
        toNode,
        NodeCategory.MODEL_CONCEPT_CLASS
      ) ||
        (isLinkLabel<ModelConceptRelationshipLabelNode>(
          toNode,
          LinkLabelCategory.MODEL_CONCEPT_RELATIONSHIP_LABEL
        ) &&
          // make sure it's a doubly-linked relationship
          toNode.data.from &&
          toNode.data.to))
    ) {
      return true;
    }

    return false;
  };

  attachAdornment(part: go.Part, to: boolean) {
    const archetype = to ? this.toHandleArchetype : this.fromHandleArchetype;
    const adornment = $(
      go.Adornment,
      {
        type: go.Panel.Link,
        adornedObject: part.selectionObject,
        mouseLeave: () => {
          this.updateAdornments(part);
        },
      },
      archetype.copy()
    );

    part.addAdornment(to ? 'RelinkTo' : 'RelinkFrom', adornment);
  }

  updateAdornments(part: go.Part) {
    if (!isLink(part)) {
      return;
    }

    const fromAdornment = part.findAdornment('RelinkFrom');
    const toAdornment = part.findAdornment('RelinkTo');

    const { targetObject } = this.diagram.lastInput;

    const isAdornmentHovered =
      targetObject?.part &&
      (targetObject.part === fromAdornment ||
        targetObject.part === toAdornment);

    const showAdornments =
      part.isHighlighted || part.isSelected || isAdornmentHovered;

    if (showAdornments) {
      if (!fromAdornment && part.canRelinkFrom()) {
        this.attachAdornment(part, false);
      }
      if (!toAdornment && part.canRelinkTo()) {
        this.attachAdornment(part, true);
      }
    } else {
      if (fromAdornment) {
        part.removeAdornment('RelinkFrom');
      }
      if (toAdornment) {
        part.removeAdornment('RelinkTo');
      }
    }
  }
}
